<script lang="ts">
    import { getContext, onDestroy } from "svelte";
    import type { Writable } from "svelte/store";
    import { CellState, type Puzzle, type Timer } from "@lib/types";
    import { hint } from "@lib/api";
    import { logEvent } from "@lib/events";

    const puzzle = getContext<Puzzle>("puzzle");
    const session = getContext<string>("session");
    const timer = getContext<Writable<Timer>>("timer");
    const placements = getContext<Writable<Array<CellState>>>("placements");
    const hintCells = getContext<Writable<Array<number>>>("hintCells");
    const hintText = getContext<Writable<string | null>>("hintText");
    const hintCooldownUntil = getContext<Writable<number>>("hintCooldownUntil");
    const puzzleError = getContext<Writable<string | null>>("puzzleError");

    const difficulty = puzzle.difficulty;
    const COOLDOWN_MS = difficulty < 10001 ? 30_000 : difficulty < 25001 ? 45_000 : 60_000;
    let cooldownRemaining = $derived(Math.max(0, $hintCooldownUntil - $timer.elapsed));

    const startCooldown = () => {
        hintCooldownUntil.set($timer.elapsed + COOLDOWN_MS);
    };

    const nextHint = async () => {
        if (cooldownRemaining > 0) return;

        const kings = $placements.reduce<Array<number>>((acc, cell, i) => {
            if (cell === CellState.Star || cell === CellState.StarInvalid) {
                acc.push(i);
            }
            return acc;
        }, []);

        const forbidden = $placements.reduce<Array<number>>((acc, cell, i) => {
            if (cell === CellState.X) {
                acc.push(i);
            }
            return acc;
        }, []);

        void logEvent({
            session,
            event_type: "hint_requested",
            elapsed_ms: $timer.elapsed,
            data: { kings_count: kings.length, forbidden_count: forbidden.length },
        });

        try {
            puzzleError.set(null);
            const h = await hint(session, kings, forbidden);
            if (h) {
                // Highlight the suggested cells and show the explanation text.
                hintCells.set(h.cells ?? []);
                hintText.set(h.text ?? "");

                void logEvent({
                    session,
                    event_type: "hint_received",
                    elapsed_ms: $timer.elapsed,
                    data: { cells_count: h.cells?.length ?? 0, valid: h.valid, solved: h.solved },
                });

                // Start a 30s cooldown after delivering a hint.
                startCooldown();
            }
        } catch (e) {
            const message =
                e instanceof Error && e.message
                    ? e.message
                    : "Unable to fetch a hint right now.";
            puzzleError.set(message);
        }
    };

    onDestroy(() => {});
</script>

<button onclick={nextHint} disabled={cooldownRemaining > 0} class:cooldown={cooldownRemaining > 0}>
    <span
        class="hint-fill"
        style={`transform: scaleX(${cooldownRemaining > 0 ? cooldownRemaining / COOLDOWN_MS : 0})`}
    ></span>
    <span class="hint-label">
        {#if cooldownRemaining > 0}
            {Math.ceil(cooldownRemaining / 1000)}
        {:else}
            Hint
        {/if}
    </span>
</button>

<style>
    button {
        position: relative;
        overflow: hidden;
        height: 1.5rem;
        padding: 0px 1em;
        font-size: 0.8em;
        border: 1px solid var(--primary);
        border-radius: 100px;
        background-color: var(--bg);
        color: var(--primary);
        font-weight: 500;
        cursor: pointer;
        text-transform: uppercase;
        min-width: 60px;
    }

    :global(.dark) button {
        border-color: var(--bg-opacity);
        background-color: var(--bg-opacity);
    }

    button[disabled] {
        cursor: not-allowed;
    }

    .hint-fill {
        position: absolute;
        inset: 0;
        background-color: var(--bg-opacity);
        transform-origin: left;
        transform: scaleX(0);
        transition: transform 0.1s linear;
        pointer-events: none;
    }

    .hint-label {
        position: relative;
        z-index: 1;
    }
</style>
